import React, { useEffect, useState, useMemo } from 'react';
import { User } from '../types';
import { authService } from '../services/authService';
import { t } from '../services/i18n';
import { formatCurrency, formatNumber, formatDate, formatDateTime } from '../src/utils/i18nFormatters';
import { useLanguageUpdate } from '../src/hooks/useLanguageChange';
import { Users, DollarSign, Activity, Plus, CheckCircle, XCircle, Edit, Trash2, CreditCard, RefreshCw, Link2, AlertTriangle, Settings, Eye, Ban, Unlock, History, Phone, Mail, Calendar, Shield, Download } from 'lucide-react';

export const AdminPanel: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [activeTab, setActiveTab] = useState<'analytics' | 'users' | 'payments' | 'settings'>('analytics');
  
  // User filters
  const [userFilters, setUserFilters] = useState({
    search: '',
    plan: 'all',
    emailVerified: 'all',
    lowCredits: false,
    usedCredits: false,
    paidPlans: false,
    startDate: '',
    endDate: ''
  });
  
  // Payment filters
  const [paymentFilters, setPaymentFilters] = useState({
    search: '',
    plan: 'all',
    userId: '',
    startDate: '',
    endDate: ''
  });
  
  const [usersTotal, setUsersTotal] = useState(0);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [settings, setSettings] = useState<{
    signupLimitType: 'unlimited' | 'daily' | 'monthly';
    signupLimitValue: number;
    paypalClientId?: string;
    paypalClientSecret?: string;
    paypalEnvironment?: 'sandbox' | 'production';
    paypalClientIdConfigured?: boolean;
    paypalClientSecretConfigured?: boolean;
  } | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [testingPayPal, setTestingPayPal] = useState(false);
  const [paypalTestResult, setPaypalTestResult] = useState<{ success: boolean; message: string; error?: string } | null>(null);
  
  // Add user form state
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'admin',
    credits: 10,
    plan: 'free' as 'free' | 'weekly' | 'monthly' | 'yearly',
    isApproved: true
  });

  // Edit user state
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<Partial<User>>({});
  
  // User detail view state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userConsumption, setUserConsumption] = useState<any>(null);
  const [userActivity, setUserActivity] = useState<any[]>([]);
  const [loadingConsumption, setLoadingConsumption] = useState(false);

  useEffect(() => {
    loadData();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoadingSettings(true);
    try {
      // Ensure API_URL ends with /api (same logic as services/api.ts)
      const API_URL = import.meta.env.VITE_API_URL 
        ? (import.meta.env.VITE_API_URL.endsWith('/api') 
            ? import.meta.env.VITE_API_URL 
            : `${import.meta.env.VITE_API_URL}/api`)
        : '/api';
      const response = await fetch(`${API_URL}/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        // If settings don't exist, create default ones
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to load settings:', errorData);
        // Initialize with default settings
        setSettings({
          signupLimitType: 'unlimited',
          signupLimitValue: 0
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Initialize with default settings on error
      setSettings({
        signupLimitType: 'unlimited',
        signupLimitValue: 0
      });
    } finally {
      setLoadingSettings(false);
    }
  };

  const updateSettings = async () => {
    if (!settings) return;
    setLoadingSettings(true);
    try {
      // Ensure API_URL ends with /api (same logic as services/api.ts)
      const API_URL = import.meta.env.VITE_API_URL 
        ? (import.meta.env.VITE_API_URL.endsWith('/api') 
            ? import.meta.env.VITE_API_URL 
            : `${import.meta.env.VITE_API_URL}/api`)
        : '/api';
      console.log('[Settings] Updating settings:', settings);
      console.log('[Settings] API URL:', `${API_URL}/settings`);
      
      const response = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      console.log('[Settings] Response status:', response.status);
      console.log('[Settings] Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Settings] Settings updated:', data);
        alert(t('admin.settingsUpdated') || 'Settings updated successfully!');
        await loadSettings();
      } else {
        const error = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
        console.error('[Settings] Update failed:', error);
        alert(`${t('admin.updateSettingsFailed') || 'Failed to update settings'}: ${error.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('[Settings] Update error:', error);
      // More detailed error message
      const errorMessage = error.message || 'Network error. Please check your connection and try again.';
      alert(`${t('admin.updateSettingsFailed') || 'Failed to update settings'}: ${errorMessage}`);
    } finally {
      setLoadingSettings(false);
    }
  };

  const checkPayPalStatus = async () => {
    setCheckingStatus(true);
    try {
      // Ensure API_URL ends with /api (same logic as services/api.ts)
      const API_URL = import.meta.env.VITE_API_URL 
        ? (import.meta.env.VITE_API_URL.endsWith('/api') 
            ? import.meta.env.VITE_API_URL 
            : `${import.meta.env.VITE_API_URL}/api`)
        : '/api';
      const response = await fetch(`${API_URL}/payments/status`, {
        headers: {
          // Cookies sent automatically with credentials: 'include'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPaypalStatus(data);
        // Set selected environment based on current active environment
        if (data.current) {
          setSelectedEnvironment(data.current as 'sandbox' | 'production');
        }
      } else {
        setPaypalStatus({
          sandbox: { connected: false, environment: 'sandbox', clientId: '', error: 'Failed to check status' },
          production: { connected: false, environment: 'production', clientId: '', error: 'Failed to check status' },
          current: 'sandbox'
        });
        setSelectedEnvironment('sandbox');
      }
    } catch (error) {
      console.error('Failed to check PayPal status:', error);
      setPaypalStatus({
        sandbox: { connected: false, environment: 'sandbox', clientId: '', error: 'Connection error' },
        production: { connected: false, environment: 'production', clientId: '', error: 'Connection error' },
        current: 'sandbox'
      });
      setSelectedEnvironment('sandbox');
    } finally {
      setCheckingStatus(false);
    }
  };

  const testPayPalConnection = async () => {
    if (!settings) return;
    setTestingPayPal(true);
    setPaypalTestResult(null);
    try {
      const API_URL = import.meta.env.VITE_API_URL
        ? (import.meta.env.VITE_API_URL.endsWith('/api')
            ? import.meta.env.VITE_API_URL
            : `${import.meta.env.VITE_API_URL}/api`)
        : '/api';
      const response = await fetch(`${API_URL}/settings/paypal/test`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      setPaypalTestResult(result);
      if (result.success) {
        alert(`✅ ${result.message}`);
      } else {
        alert(`❌ ${result.error || result.message}`);
      }
    } catch (error: any) {
      setPaypalTestResult({ success: false, message: error.message || 'Failed to test connection' });
      alert(`❌ Error testing PayPal connection: ${error.message || 'Unknown error'}`);
    } finally {
      setTestingPayPal(false);
    }
  };

  const handleEnvironmentSwitch = async (env: 'sandbox' | 'production') => {
    const confirmMessage = env === 'production' 
      ? t('admin.switchToProductionConfirm') || `Switch to LIVE (Production) mode?\n\n⚠️ WARNING: This will enable REAL payments!\n\nThis will update the .env file automatically.`
      : t('admin.switchToSandboxConfirm') || `Switch to Sandbox (Testing) mode?\n\n✓ Safe testing mode - no real charges.\n\nThis will update the .env file automatically.`;
    if (!confirm(confirmMessage)) {
      return;
    }
    
    try {
      // Ensure API_URL ends with /api (same logic as services/api.ts)
      const API_URL = import.meta.env.VITE_API_URL 
        ? (import.meta.env.VITE_API_URL.endsWith('/api') 
            ? import.meta.env.VITE_API_URL 
            : `${import.meta.env.VITE_API_URL}/api`)
        : '/api';
      const response = await fetch(`${API_URL}/payments/switch-environment`, {
        method: 'POST',
        credentials: 'include' as RequestCredentials, // Include cookies
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ environment: env })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.needsRestart) {
          const shouldRestart = confirm(`${t('admin.restartConfirm') || '✅ Backend needs restart'}\n\n${t('admin.restartQuestion') || 'Would you like to restart the backend server now?'}\n\n${t('admin.restartInstructions') || 'Click OK to restart automatically, or Cancel to restart manually later.'}`);
          if (shouldRestart) {
            // Call restart endpoint
            try {
              const restartResponse = await fetch(`${API_URL}/payments/restart-backend`, {
                method: 'POST',
                credentials: 'include' as RequestCredentials, // Include cookies
                headers: {
                  'Content-Type': 'application/json'
                }
              });
              if (restartResponse.ok) {
                alert(t('admin.restartInitiated') || '✅ Backend server restart initiated. Please wait a few seconds and refresh the page.');
                // Wait a bit then refresh status
                setTimeout(() => {
                  window.location.reload();
                }, 3000);
              } else {
                alert(t('admin.restartManual') || '⚠️ Could not restart automatically. Please restart the backend server manually.');
              }
            } catch (restartError) {
              alert(t('admin.restartManualCommand') || '⚠️ Could not restart automatically. Please restart the backend server manually using: cd server && npm run dev');
            }
          }
        } else {
          alert(`✅ ${data.message || t('admin.environmentSwitched') || 'Environment switched successfully'}`);
        }
        setSelectedEnvironment(env);
        // Refresh status after switch
        setTimeout(() => checkPayPalStatus(), 1000);
      } else {
        const error = await response.json();
        alert(`❌ ${error.error || t('admin.switchFailed') || 'Failed to switch environment'}\n\n${error.details || ''}`);
      }
    } catch (error) {
      console.error('Failed to switch environment:', error);
      alert(t('admin.switchConnectionError') || '❌ Failed to switch environment. Please check server connection.');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const userFiltersParams: any = {};
      if (userFilters.search) userFiltersParams.search = userFilters.search;
      if (userFilters.plan !== 'all') userFiltersParams.plan = userFilters.plan;
      if (userFilters.emailVerified !== 'all') userFiltersParams.emailVerified = userFilters.emailVerified;
      if (userFilters.lowCredits) userFiltersParams.lowCredits = 'true';
      if (userFilters.usedCredits) userFiltersParams.usedCredits = 'true';
      if (userFilters.paidPlans) userFiltersParams.paidPlans = 'true';
      if (userFilters.startDate) userFiltersParams.startDate = userFilters.startDate;
      if (userFilters.endDate) userFiltersParams.endDate = userFilters.endDate;
      
      const paymentFiltersParams: any = {};
      if (paymentFilters.search) paymentFiltersParams.search = paymentFilters.search;
      if (paymentFilters.plan !== 'all') paymentFiltersParams.plan = paymentFilters.plan;
      if (paymentFilters.userId) paymentFiltersParams.userId = paymentFilters.userId;
      if (paymentFilters.startDate) paymentFiltersParams.startDate = paymentFilters.startDate;
      if (paymentFilters.endDate) paymentFiltersParams.endDate = paymentFilters.endDate;
      
      const [usersResult, paymentsResult] = await Promise.all([
        authService.getAllUsers(userFiltersParams),
        authService.getPaymentHistory(paymentFiltersParams).catch(() => ({ payments: [], total: 0, limit: 100, skip: 0 }))
      ]);
      // Ensure users is always set to a valid array
      const usersArray = usersResult?.users;
      if (Array.isArray(usersArray)) {
        setUsers(usersArray);
      } else {
        console.warn('[AdminPanel] Invalid users data received:', usersArray);
        setUsers([]);
      }
      setUsersTotal(usersResult?.total || 0);
      setPayments(paymentsResult.payments || []);
      setPaymentsTotal(paymentsResult.total || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert(t('admin.loadDataFailed') || 'Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await authService.createUser(newUser);
      alert(t('admin.userCreated') || 'User created successfully!');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'user', credits: 10, plan: 'free', isApproved: true });
      loadData();
    } catch (error: any) {
      alert(`${t('admin.createUserFailed') || 'Failed to create user'}: ${error.message || t('admin.unknownError') || 'Unknown error'}`);
    }
  };

  const handleApproveUser = async (userId: string | undefined, isApproved: boolean) => {
    if (!userId) {
      alert('Error: User ID is missing');
      return;
    }
    try {
      await authService.approveUser(userId, isApproved);
      alert(isApproved ? (t('admin.userApproved') || 'User approved successfully!') : (t('admin.userDisapproved') || 'User disapproved successfully!'));
      loadData();
    } catch (error: any) {
      alert(`${t('admin.updateApprovalFailed') || 'Failed to update approval'}: ${error.message || t('admin.unknownError') || 'Unknown error'}`);
    }
  };

  const handleUpdateCredits = async (userId: string | undefined, credits: number) => {
    if (!userId) {
      alert('Error: User ID is missing');
      return;
    }
    try {
      await authService.updateUserCredits(userId, credits);
      alert(t('admin.creditsUpdated') || 'Credits updated successfully!');
      loadData();
    } catch (error: any) {
      alert(`${t('admin.updateCreditsFailed') || 'Failed to update credits'}: ${error.message || t('admin.unknownError') || 'Unknown error'}`);
    }
  };

  const handleUpdateUser = async (userId: string | undefined) => {
    if (!userId) {
      alert('Error: User ID is missing');
      return;
    }
    try {
      await authService.updateUser(userId, editForm);
      alert(t('admin.userUpdated') || 'User updated successfully!');
      setEditingUser(null);
      setEditForm({});
      loadData();
    } catch (error: any) {
      alert(`${t('admin.updateUserFailed') || 'Failed to update user'}: ${error.message || t('admin.unknownError') || 'Unknown error'}`);
    }
  };

  const handleDeleteUser = async (userId: string | undefined) => {
    if (!userId) {
      alert('Error: User ID is missing');
      return;
    }
    if (!confirm(t('admin.deleteUserConfirm') || 'Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    try {
      await authService.deleteUser(userId);
      alert(t('admin.userDeleted') || 'User deleted successfully!');
      loadData();
    } catch (error: any) {
      alert(`${t('admin.deleteUserFailed') || 'Failed to delete user'}: ${error.message || t('admin.unknownError') || 'Unknown error'}`);
    }
  };

  // Safety check: ensure users and payments are always valid arrays using useMemo
  const safeUsers = useMemo(() => {
    try {
      if (users === null || users === undefined) {
        return [];
      }
      if (Array.isArray(users)) {
        return users;
      }
      console.warn('[AdminPanel] users is not an array:', typeof users, users);
      return [];
    } catch (error) {
      console.error('[AdminPanel] Error creating safeUsers:', error);
      return [];
    }
  }, [users]);
  
  const safePayments = useMemo(() => {
    try {
      if (payments === null || payments === undefined) {
        return [];
      }
      if (Array.isArray(payments)) {
        return payments;
      }
      console.warn('[AdminPanel] payments is not an array:', typeof payments, payments);
      return [];
    } catch (error) {
      console.error('[AdminPanel] Error creating safePayments:', error);
      return [];
    }
  }, [payments]);
  
  // Calculate total revenue safely
  const totalRevenue = useMemo(() => {
    try {
      // Multiple checks to ensure safeUsers is a valid array
      if (!safeUsers || !Array.isArray(safeUsers) || safeUsers.length === 0) {
        return 0;
      }
      
      return safeUsers.reduce((acc: number, user: any) => {
        if (!user || typeof user !== 'object' || !('plan' in user)) {
          return acc;
        }
    if (user.plan === 'weekly') return acc + 10;
    if (user.plan === 'monthly') return acc + 29;
    if (user.plan === 'yearly') return acc + 290;
    return acc;
  }, 0);
    } catch (error) {
      console.error('[AdminPanel] Error calculating totalRevenue:', error, { 
        users, 
        safeUsers,
        usersType: typeof users, 
        safeUsersType: typeof safeUsers,
        isArray: Array.isArray(users),
        safeUsersIsArray: Array.isArray(safeUsers),
        hasReduce: typeof safeUsers?.reduce === 'function'
      });
      return 0;
    }
  }, [safeUsers]);
  
  // Get user's preferred country for currency formatting
  const getCurrentUserCountry = (): string | undefined => {
    try {
      const userStr = localStorage.getItem('orca_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.preferredCountry;
      }
    } catch (e) {
      // Ignore
    }
    return undefined;
  };

  // Calculate approved and pending users safely
  const approvedUsers = useMemo(() => {
    if (!safeUsers || !Array.isArray(safeUsers)) return 0;
    try {
      return safeUsers.filter(u => u && typeof u === 'object' && u.isApproved !== false).length;
    } catch (error) {
      console.error('[AdminPanel] Error calculating approvedUsers:', error);
      return 0;
    }
  }, [safeUsers]);
  
  const pendingUsers = useMemo(() => {
    if (!safeUsers || !Array.isArray(safeUsers)) return 0;
    try {
      return safeUsers.filter(u => u && typeof u === 'object' && u.isApproved === false).length;
    } catch (error) {
      console.error('[AdminPanel] Error calculating pendingUsers:', error);
      return 0;
    }
  }, [safeUsers]);

  const exportUsersToCSV = () => {
    try {
      const headers = [
        'Name',
        'Email',
        'WhatsApp Number',
        'Role',
        'Plan',
        'Credits',
        'Status',
        'Is Kicked',
        'Email Verified',
        'Preferred Language',
        'UI Language',
        'AI Assistant Language',
        'Preferred Country',
        'Joined Date',
        'Last Login'
      ];

      // Use safeUsers instead of users
      if (!safeUsers || !Array.isArray(safeUsers) || safeUsers.length === 0) {
        console.warn('[AdminPanel] Cannot export: no valid users data');
        alert('No users data available to export');
        return;
      }

      const rows = safeUsers.map(user => {
        if (!user || typeof user !== 'object') {
          return [];
        }
        return [
          user.name || '',
          user.email || '',
          user.whatsappNumber || '',
          user.role || 'user',
          user.plan || 'free',
          formatNumber(user.credits || 0),
          user.isApproved === false ? 'Pending' : user.isKicked ? 'Kicked' : 'Approved',
          user.isKicked ? 'Yes' : 'No',
          user.emailVerified ? 'Yes' : 'No',
          (user as any).preferredLanguage || '',
          (user as any).uiLanguage || '',
          (user as any).aiAssistantLanguage || '',
          (user as any).preferredCountry || '',
          user.createdAt ? formatDate(user.createdAt, { year: 'numeric', month: '2-digit', day: '2-digit' }) : '',
          user.lastLogin ? formatDate(user.lastLogin, { year: 'numeric', month: '2-digit', day: '2-digit' }) : ''
        ];
      }).filter(row => Array.isArray(row) && row.length > 0); // Filter out invalid rows

      if (!Array.isArray(rows) || rows.length === 0) {
        alert('No valid user data to export');
        return;
      }

      const csvContent = [
        headers.join(','),
        ...rows.map(row => {
          if (!Array.isArray(row)) return '';
          return row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',');
        })
      ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const dateStr = formatDate(new Date(), { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-');
    link.setAttribute('download', `users_export_${dateStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    } catch (error) {
      console.error('[AdminPanel] Error exporting users to CSV:', error);
      alert('Failed to export users. Please try again.');
    }
  };

  return (
    <div className="p-8 animate-fadeIn text-white">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold">Admin Command Center</h2>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
           <div className="flex items-center gap-4">
             <div className="p-3 bg-indigo-500/20 rounded-lg text-indigo-400"><Users /></div>
             <div>
              <div className="text-2xl font-bold">{formatNumber(safeUsers.length)}</div>
              <div className="text-slate-400 text-sm">{t('admin.totalUsers') || 'Total Users'}</div>
             </div>
           </div>
        </div>
        <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
           <div className="flex items-center gap-4">
             <div className="p-3 bg-emerald-500/20 rounded-lg text-emerald-400"><DollarSign /></div>
             <div>
              <div className="text-2xl font-bold">{formatCurrency(totalRevenue, getCurrentUserCountry())}</div>
              <div className="text-slate-400 text-sm">{t('admin.totalRevenue') || 'Total Revenue (Est)'}</div>
             </div>
           </div>
        </div>
        <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
           <div className="flex items-center gap-4">
             <div className="p-3 bg-blue-500/20 rounded-lg text-blue-400"><Activity /></div>
             <div>
              <div className="text-2xl font-bold">{formatNumber(safeUsers.filter(u => u && u.plan !== 'free').length)}</div>
              <div className="text-slate-400 text-sm">{t('admin.activeSubscribers') || 'Active Subscribers'}</div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-500/20 rounded-lg text-yellow-400"><CheckCircle /></div>
            <div>
              <div className="text-2xl font-bold">{approvedUsers}/{pendingUsers}</div>
              <div className="text-slate-400 text-sm">Approved/Pending</div>
             </div>
           </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-700">
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
            activeTab === 'analytics'
              ? 'border-indigo-500 text-indigo-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Activity size={18} className="inline mr-2" />
          Analytics
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
            activeTab === 'users'
              ? 'border-indigo-500 text-indigo-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Users size={18} className="inline mr-2" />
          Users Management
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
            activeTab === 'payments'
              ? 'border-indigo-500 text-indigo-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <DollarSign size={18} className="inline mr-2" />
          Payment History
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
            activeTab === 'settings'
              ? 'border-indigo-500 text-indigo-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Settings size={18} className="inline mr-2" />
          Settings
        </button>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
              <div className="text-3xl font-bold text-white mb-2">{safeUsers.length}</div>
              <div className="text-slate-400">Total Users</div>
            </div>
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
              <div className="text-3xl font-bold text-emerald-400 mb-2">{approvedUsers}</div>
              <div className="text-slate-400">Approved Users</div>
            </div>
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
              <div className="text-3xl font-bold text-yellow-400 mb-2">{pendingUsers}</div>
              <div className="text-slate-400">Pending Approval</div>
            </div>
          </div>

      <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-700">
              <h3 className="font-bold text-lg">User Management Actions</h3>
              <p className="text-sm text-slate-400 mt-1">Ban, remove, or kick users from the system</p>
        </div>
            <div className="p-6">
              <div className="space-y-4">
                {safeUsers.map((user) => {
                  const userId = user.id || (user as any)._id;
                  return (
                    <div key={userId} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="flex-1">
                        <div className="font-medium text-white">{user.name}</div>
                        <div className="text-sm text-slate-400">{user.email}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Role: {user.role} | Plan: {user.plan} | Credits: {user.credits}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            if (!confirm(`Ban ${user.name}? They will not be able to login.`)) return;
                            try {
                              await authService.updateUser(userId, { isApproved: false, credits: 0 });
                              alert('User banned successfully!');
                              loadData();
                            } catch (error: any) {
                              alert(`Failed to ban user: ${error.message}`);
                            }
                          }}
                          className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm font-medium transition-colors"
                          title="Ban user (disable login)"
                        >
                          Ban
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(`Remove ${user.name} from database? This cannot be undone!`)) return;
                            try {
                              await authService.deleteUser(userId);
                              alert('User removed from database!');
                              loadData();
                            } catch (error: any) {
                              alert(`Failed to remove user: ${error.message}`);
                            }
                          }}
                          className="px-3 py-1.5 bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 rounded-lg text-sm font-medium transition-colors"
                          title="Remove from database"
                        >
                          Remove
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(`Kick ${user.name} off? They will be logged out immediately.`)) return;
                            try {
                              await authService.updateUser(userId, { isApproved: false });
                              alert('User kicked off successfully! They will be logged out on next request.');
                              loadData();
                            } catch (error: any) {
                              alert(`Failed to kick user: ${error.message}`);
                            }
                          }}
                          className="px-3 py-1.5 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 rounded-lg text-sm font-medium transition-colors"
                          title="Kick off (force logout)"
                        >
                          Kick Off
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Add User Button */}
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddUser(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors"
            >
              <Plus size={18} />
              Add New User
            </button>
          </div>

          {/* Add User Modal */}
          {showAddUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4">
                <h3 className="text-xl font-bold mb-4">Add New User</h3>
                <form onSubmit={handleAddUser} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                    required
                    minLength={8}
                  />
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'user' | 'admin' })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Credits"
                    value={newUser.credits}
                    onChange={(e) => setNewUser({ ...newUser, credits: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                    min="0"
                  />
                  <select
                    value={newUser.plan}
                    onChange={(e) => setNewUser({ ...newUser, plan: e.target.value as any })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  >
                    <option value="free">Free</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <label className="flex items-center gap-2 text-slate-300">
                    <input
                      type="checkbox"
                      checked={newUser.isApproved}
                      onChange={(e) => setNewUser({ ...newUser, isApproved: e.target.checked })}
                      className="rounded"
                    />
                    Approved
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                    >
                      Create User
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddUser(false)}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* User Filters */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Settings size={20} />
              Filter Users
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Name, Email, WhatsApp..."
                  value={userFilters.search}
                  onChange={(e) => setUserFilters({ ...userFilters, search: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Plan</label>
                <select
                  value={userFilters.plan}
                  onChange={(e) => setUserFilters({ ...userFilters, plan: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                >
                  <option value="all">All Plans</option>
                  <option value="free">Free</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Email Verified</label>
                <select
                  value={userFilters.emailVerified}
                  onChange={(e) => setUserFilters({ ...userFilters, emailVerified: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                >
                  <option value="all">All</option>
                  <option value="true">Verified</option>
                  <option value="false">Not Verified</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Registration Date From</label>
                <input
                  type="date"
                  value={userFilters.startDate}
                  onChange={(e) => setUserFilters({ ...userFilters, startDate: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Registration Date To</label>
                <input
                  type="date"
                  value={userFilters.endDate}
                  onChange={(e) => setUserFilters({ ...userFilters, endDate: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userFilters.lowCredits}
                    onChange={(e) => setUserFilters({ ...userFilters, lowCredits: e.target.checked })}
                    className="rounded"
                  />
                  Low Credits (&lt;5)
                </label>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userFilters.usedCredits}
                    onChange={(e) => setUserFilters({ ...userFilters, usedCredits: e.target.checked })}
                    className="rounded"
                  />
                  Used Credits
                </label>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userFilters.paidPlans}
                    onChange={(e) => setUserFilters({ ...userFilters, paidPlans: e.target.checked })}
                    className="rounded"
                  />
                  Paid Plans Only
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={loadData}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Apply Filters
              </button>
              <button
                onClick={() => {
                  setUserFilters({
                    search: '',
                    plan: 'all',
                    emailVerified: 'all',
                    lowCredits: false,
                    usedCredits: false,
                    paidPlans: false,
                    startDate: '',
                    endDate: ''
                  });
                  setTimeout(loadData, 100);
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
              >
                Reset
              </button>
              <div className="ml-auto text-sm text-slate-400 flex items-center">
                Total: {usersTotal} users
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="font-bold text-lg">Registered Users ({usersTotal})</h3>
              <button
                onClick={exportUsersToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-all"
              >
                <Download size={18} />
                Export CSV
              </button>
            </div>
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading...</div>
            ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-400">
            <thead className="bg-slate-900/50 text-slate-200 uppercase font-bold text-xs">
              <tr>
                <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Contact</th>
                      <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4">Plan</th>
                <th className="px-6 py-4">Credits</th>
                <th className="px-6 py-4">Joined</th>
                      <th className="px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
                    {safeUsers.map((user) => {
                      const userId = user.id || (user as any)._id;
                      return (
                      <tr key={userId} className={`hover:bg-slate-700/50 transition-colors ${user.isKicked ? 'bg-red-500/10' : ''}`}>
                  <td className="px-6 py-4 font-medium text-white">
                          <div className="flex items-center gap-2">
                            {user.isKicked && <Ban size={16} className="text-red-400" />}
                            <div>
                    <div>{user.name}</div>
                    <div className="text-xs text-slate-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs">
                            {user.whatsappNumber ? (
                              <div className="flex items-center gap-1 text-slate-300">
                                <Phone size={12} />
                                {user.whatsappNumber}
                              </div>
                            ) : (
                              <span className="text-slate-500">No WhatsApp</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            {user.isKicked ? (
                              <span className="px-2 py-1 rounded text-xs font-bold uppercase bg-red-500/20 text-red-400">
                                Kicked
                              </span>
                            ) : user.isApproved === false ? (
                              <span className="px-2 py-1 rounded text-xs font-bold uppercase bg-yellow-500/20 text-yellow-400">
                                Pending
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-bold uppercase bg-emerald-500/20 text-emerald-400">
                                Approved
                              </span>
                            )}
                            {user.emailVerified ? (
                              <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">Verified</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-xs bg-slate-600/20 text-slate-400">Unverified</span>
                            )}
                          </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${user.role === 'admin' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-600/20 text-slate-400'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                     <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${user.plan === 'free' ? 'bg-slate-600/20 text-slate-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                      {user.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                    <span className={user.credits === 0 ? 'text-red-400 font-bold' : 'text-slate-300'}>
                      {user.credits}
                    </span>
                            <button
                              onClick={() => {
                                const newCredits = prompt('Enter new credit amount:', formatNumber(user.credits || 0));
                                if (newCredits !== null) {
                                  handleUpdateCredits(userId, parseInt(newCredits) || 0);
                                }
                              }}
                              className="text-indigo-400 hover:text-indigo-300"
                              title="Edit credits"
                            >
                              <Edit size={14} />
                            </button>
                          </div>
                  </td>
                        <td className="px-6 py-4 text-xs">{user.createdAt ? formatDate(user.createdAt, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 flex-wrap">
                            <button
                              onClick={() => handleViewUserDetails(user)}
                              className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                              title="View details & history"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => handleApproveUser(userId, !user.isApproved)}
                              className={`p-1.5 rounded transition-colors ${
                                user.isApproved === false
                                  ? 'text-emerald-400 hover:bg-emerald-500/20'
                                  : 'text-yellow-400 hover:bg-yellow-500/20'
                              }`}
                              title={user.isApproved === false ? 'Approve' : 'Disapprove'}
                            >
                              {user.isApproved === false ? <CheckCircle size={16} /> : <XCircle size={16} />}
                            </button>
                            {user.isKicked ? (
                              <button
                                onClick={() => handleRestoreUser(userId)}
                                className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded transition-colors"
                                title="Restore access"
                              >
                                <Unlock size={16} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleKickUser(userId, user.kickReason || undefined)}
                                className="p-1.5 text-orange-400 hover:bg-orange-500/20 rounded transition-colors"
                                title="Kick user (block access)"
                              >
                                <Ban size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setEditingUser(user);
                                setEditForm({ name: user.name, email: user.email, role: user.role, plan: user.plan, whatsappNumber: user.whatsappNumber });
                              }}
                              className="p-1.5 text-indigo-400 hover:bg-indigo-500/20 rounded transition-colors"
                              title="Edit user"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(userId)}
                              className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                              title="Delete user permanently"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold flex items-center gap-3">
                <Settings size={24} />
                Admin Settings
              </h3>
            </div>

            {loadingSettings ? (
              <div className="text-center py-8 text-slate-400">
                <RefreshCw className="w-12 h-12 mx-auto mb-4 animate-spin" />
                <p>Loading settings...</p>
              </div>
            ) : settings ? (
              <div className="space-y-6">
                {/* Signup Limits Section */}
                <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700">
                  <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Users size={20} />
                    Signup Limits
                  </h4>
                  <p className="text-slate-400 text-sm mb-6">
                    Control how many users can sign up per day or month. Set to unlimited to allow all signups.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-white mb-2">
                        Limit Type
                      </label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setSettings({ ...settings, signupLimitType: 'unlimited', signupLimitValue: 0 })}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            settings.signupLimitType === 'unlimited'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Unlimited
                        </button>
                        <button
                          onClick={() => setSettings({ ...settings, signupLimitType: 'daily', signupLimitValue: settings.signupLimitValue || 10 })}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            settings.signupLimitType === 'daily'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Daily
                        </button>
                        <button
                          onClick={() => setSettings({ ...settings, signupLimitType: 'monthly', signupLimitValue: settings.signupLimitValue || 100 })}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            settings.signupLimitType === 'monthly'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Monthly
                        </button>
                      </div>
                    </div>

                    {settings.signupLimitType !== 'unlimited' && (
                      <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                          Maximum Signups {settings.signupLimitType === 'daily' ? 'Per Day' : 'Per Month'}
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={settings.signupLimitValue || 0}
                          onChange={(e) => setSettings({ ...settings, signupLimitValue: parseInt(e.target.value) || 0 })}
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                          placeholder={settings.signupLimitType === 'daily' ? 'e.g., 10' : 'e.g., 100'}
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          When this limit is reached, new signups will be blocked until the next {settings.signupLimitType === 'daily' ? 'day' : 'month'}.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={updateSettings}
                        disabled={loadingSettings}
                        className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {loadingSettings ? (
                          <>
                            <RefreshCw size={18} className="animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={18} />
                            Save Settings
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* PayPal Configuration Section */}
                <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700 mt-6">
                  <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <CreditCard size={20} />
                    PayPal Configuration
                  </h4>
                  <p className="text-slate-400 text-sm mb-6">
                    Configure your PayPal credentials to enable payment verification. These credentials are stored securely in the database.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-white mb-2">
                        PayPal Environment
                      </label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setSettings({ ...settings, paypalEnvironment: 'sandbox' })}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            settings.paypalEnvironment === 'sandbox'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Sandbox (Testing)
                        </button>
                        <button
                          onClick={() => setSettings({ ...settings, paypalEnvironment: 'production' })}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            settings.paypalEnvironment === 'production'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          Production (Live)
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-white mb-2">
                        PayPal Client ID
                      </label>
                      <input
                        type="text"
                        value={settings.paypalClientId || ''}
                        onChange={(e) => setSettings({ ...settings, paypalClientId: e.target.value })}
                        placeholder="Enter PayPal Client ID"
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                      />
                      {settings.paypalClientIdConfigured && !settings.paypalClientId && (
                        <p className="text-xs text-yellow-400 mt-1">
                          ⚠️ Client ID is currently configured. Leave empty to keep current value, or enter new value to update.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-white mb-2">
                        PayPal Client Secret
                      </label>
                      <input
                        type="password"
                        value={settings.paypalClientSecret || ''}
                        onChange={(e) => setSettings({ ...settings, paypalClientSecret: e.target.value })}
                        placeholder={settings.paypalClientSecretConfigured ? "Enter new secret to update, or leave empty to keep current" : "Enter PayPal Client Secret"}
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                      />
                      {settings.paypalClientSecretConfigured && (
                        <p className="text-xs text-slate-400 mt-1">
                          Secret is configured. Enter new value to update, or leave empty to keep current.
                        </p>
                      )}
                    </div>

                    {paypalTestResult && (
                      <div className={`p-3 rounded-lg flex items-start gap-2 ${
                        paypalTestResult.success 
                          ? 'bg-emerald-500/10 border border-emerald-500/30' 
                          : 'bg-red-500/10 border border-red-500/30'
                      }`}>
                        {paypalTestResult.success ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className={`text-sm font-semibold ${
                            paypalTestResult.success ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {paypalTestResult.success ? '✅ Connection Successful' : '❌ Connection Failed'}
                          </p>
                          <p className={`text-xs mt-1 ${
                            paypalTestResult.success ? 'text-emerald-300' : 'text-red-300'
                          }`}>
                            {paypalTestResult.message}
                          </p>
                          {paypalTestResult.error && (
                            <p className="text-xs mt-1 text-red-300">
                              {paypalTestResult.error}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={updateSettings}
                        disabled={loadingSettings}
                        className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {loadingSettings ? (
                          <>
                            <RefreshCw size={18} className="animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={18} />
                            Save PayPal Credentials
                          </>
                        )}
                      </button>
                      <button
                        onClick={testPayPalConnection}
                        disabled={testingPayPal || !settings.paypalClientId || !settings.paypalClientSecret}
                        className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {testingPayPal ? (
                          <>
                            <RefreshCw size={18} className="animate-spin" />
                            Testing...
                          </>
                        ) : (
                          <>
                            <Link2 size={18} />
                            Test Connection
                          </>
                        )}
                      </button>
                    </div>

                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-4">
                      <p className="text-yellow-400 text-xs">
                        <strong>⚠️ Security Note:</strong> PayPal credentials are stored in the database. Make sure your database is secure and access is restricted. For production, consider using environment variables instead.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
                <p>Failed to load settings</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="space-y-6">
          {/* Payment Filters */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Settings size={20} />
              Filter Payments
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Search</label>
                <input
                  type="text"
                  placeholder="User, Email, Order ID..."
                  value={paymentFilters.search}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, search: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Plan</label>
                <select
                  value={paymentFilters.plan}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, plan: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                >
                  <option value="all">All Plans</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Date From</label>
                <input
                  type="date"
                  value={paymentFilters.startDate}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, startDate: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Date To</label>
                <input
                  type="date"
                  value={paymentFilters.endDate}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, endDate: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={loadData}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Apply Filters
              </button>
              <button
                onClick={() => {
                  setPaymentFilters({
                    search: '',
                    plan: 'all',
                    userId: '',
                    startDate: '',
                    endDate: ''
                  });
                  setTimeout(loadData, 100);
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
              >
                Reset
              </button>
              <div className="ml-auto text-sm text-slate-400 flex items-center">
                Total: {paymentsTotal} payments
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-700">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <CreditCard size={20} />
                Payment History ({paymentsTotal})
              </h3>
            </div>
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : safePayments.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No payment history available</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-400">
                <thead className="bg-slate-900/50 text-slate-200 uppercase font-bold text-xs">
                  <tr>
                    <th className="px-6 py-4">User</th>
                    <th className="px-6 py-4">Plan</th>
                    <th className="px-6 py-4">Order ID</th>
                    <th className="px-6 py-4">Expiry Date</th>
                    <th className="px-6 py-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {safePayments.map((payment, idx) => (
                    <tr key={idx} className="hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-white">
                        <div>{payment.userName}</div>
                        <div className="text-xs text-slate-500">{payment.userEmail}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded text-xs font-bold uppercase bg-emerald-500/20 text-emerald-400">
                          {payment.plan}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs">{payment.orderId || 'N/A'}</td>
                      <td className="px-6 py-4">
                        {payment.subscriptionExpiry ? formatDate(payment.subscriptionExpiry, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        {payment.createdAt ? formatDate(payment.createdAt, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                      </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          )}
      </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Edit User</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Name"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
              />
              <input
                type="email"
                placeholder="Email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
              />
              <input
                type="text"
                placeholder="WhatsApp Number"
                value={editForm.whatsappNumber || ''}
                onChange={(e) => setEditForm({ ...editForm, whatsappNumber: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
              />
              <select
                value={editForm.role || 'user'}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'user' | 'admin' })}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <select
                value={editForm.plan || 'free'}
                onChange={(e) => setEditForm({ ...editForm, plan: e.target.value as any })}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
              >
                <option value="free">Free</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const userId = editingUser.id || (editingUser as any)._id;
                    handleUpdateUser(userId);
                  }}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditingUser(null);
                    setEditForm({});
                  }}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
